/**
 * Durable, per-company marker of how far the user got in the onboarding wizard.
 *
 * The wizard step lives only in in-memory Vue state (`useOnboardingWizard`), so a
 * page reload used to drop the user back on step 1 (the website/URL screen) even
 * though the scrape was already running server-side. We persist a lightweight
 * marker here and restore it on mount so a refresh keeps the user where they were.
 *
 * Backend data (company record + genaisis session) remains the source of truth for
 * the *values*; this only remembers the *position* in the flow.
 */
export type OnboardingProgressMarker = {
  step: number
  slug: string
}

const storageKey = (companyId: string) => `onboarding:progress:${companyId}`

function safeLocalStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    // Access can throw in privacy modes / sandboxed iframes.
    return null
  }
}

export function readOnboardingProgress(
  companyId: string | null | undefined
): OnboardingProgressMarker | null {
  if (!companyId) return null
  const store = safeLocalStorage()
  if (!store) return null
  try {
    const raw = store.getItem(storageKey(companyId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<OnboardingProgressMarker>
    if (typeof parsed?.step !== 'number' || !Number.isFinite(parsed.step)) return null
    return { step: parsed.step, slug: typeof parsed.slug === 'string' ? parsed.slug : '' }
  } catch {
    return null
  }
}

export function writeOnboardingProgress(
  companyId: string | null | undefined,
  marker: OnboardingProgressMarker
): void {
  if (!companyId) return
  const store = safeLocalStorage()
  if (!store) return
  try {
    store.setItem(storageKey(companyId), JSON.stringify(marker))
  } catch {
    // Quota / disabled storage — best-effort only.
  }
}

/**
 * Per-company record of which one-off Heap funnel milestones (e.g.
 * "chat_reached") already fired. Component-local booleans would reset on
 * every mount, so a page reload while resuming onboarding (restored via the
 * marker above) would re-fire and double-count a milestone already reached
 * in an earlier session.
 */
const milestonesKey = (companyId: string) => `onboarding:heap-milestones:${companyId}`

/**
 * Per-company choice between the manual form and the AI chat on the enter-data
 * step. The store flag is in-memory, so without this a US user who switched to
 * the chat would land back on the (default) manual form after a reload.
 */
export type OnboardingEnterDataMode = 'chat' | 'form'

const enterDataModeKey = (companyId: string) => `onboarding:enter-data-mode:${companyId}`

export function readOnboardingEnterDataMode(
  companyId: string | null | undefined
): OnboardingEnterDataMode | null {
  if (!companyId) return null
  const store = safeLocalStorage()
  if (!store) return null
  try {
    const raw = store.getItem(enterDataModeKey(companyId))
    return raw === 'chat' || raw === 'form' ? raw : null
  } catch {
    return null
  }
}

export function writeOnboardingEnterDataMode(
  companyId: string | null | undefined,
  mode: OnboardingEnterDataMode
): void {
  if (!companyId) return
  const store = safeLocalStorage()
  if (!store) return
  try {
    store.setItem(enterDataModeKey(companyId), mode)
  } catch {
    // Quota / disabled storage — best-effort only.
  }
}

/**
 * Per-company legal-consent checkboxes ticked before the final submit. They are
 * only sent to the backend on "Complete Setup", so without this a reload
 * unticked every box on the form and the review page.
 */
const legalConsentsKey = (companyId: string) => `onboarding:legal-consents:${companyId}`

export function readOnboardingLegalConsents(
  companyId: string | null | undefined
): Record<string, boolean> | null {
  if (!companyId) return null
  const store = safeLocalStorage()
  if (!store) return null
  try {
    const raw = store.getItem(legalConsentsKey(companyId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
    return Object.fromEntries(
      Object.entries(parsed).filter(([, value]) => typeof value === 'boolean')
    ) as Record<string, boolean>
  } catch {
    return null
  }
}

export function writeOnboardingLegalConsents(
  companyId: string | null | undefined,
  consents: Record<string, boolean>
): void {
  if (!companyId) return
  const store = safeLocalStorage()
  if (!store) return
  try {
    store.setItem(legalConsentsKey(companyId), JSON.stringify(consents))
  } catch {
    // Quota / disabled storage — best-effort only.
  }
}

export function clearOnboardingProgress(companyId: string | null | undefined): void {
  if (!companyId) return
  const store = safeLocalStorage()
  if (!store) return
  try {
    store.removeItem(storageKey(companyId))
    store.removeItem(milestonesKey(companyId))
    store.removeItem(enterDataModeKey(companyId))
    store.removeItem(legalConsentsKey(companyId))
  } catch {
    // no-op
  }
}

export function readTrackedOnboardingMilestones(companyId: string | null | undefined): Set<string> {
  if (!companyId) return new Set()
  const store = safeLocalStorage()
  if (!store) return new Set()
  try {
    const raw = store.getItem(milestonesKey(companyId))
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    return new Set(Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [])
  } catch {
    return new Set()
  }
}

export function markOnboardingMilestoneTracked(
  companyId: string | null | undefined,
  milestone: string
): void {
  if (!companyId) return
  const store = safeLocalStorage()
  if (!store) return
  try {
    const current = readTrackedOnboardingMilestones(companyId)
    current.add(milestone)
    store.setItem(milestonesKey(companyId), JSON.stringify([...current]))
  } catch {
    // Quota / disabled storage — best-effort only.
  }
}
